import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Switch,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MenuIcon from "@mui/icons-material/Menu";

import { DefaultAndAuthProps } from "model/props";
import {
  getLanguageString as _,
  getCurrentLanguage,
  setLanguage,
} from "misc/lang";
import Logo from "assets/images/addyou_white.png";
import LogoHR from "assets/images/Logohr.png";
import {
  ConfigMap,
  SideMenuAccountAdmin,
  SideMenuHomeManager,
  SideMenuOperationsManager,
  SideMenuSuperAdmin,
  Paths,
} from "appConfig";
import css from "./sidemenu.module.scss";
import { signOut } from "misc/auth_utils";

const DRAWER_WIDTH = 280;

const switchSx = {
  margin: "0 8px",
  padding: 0,
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#3E939F !important",
    opacity: "1 !important",
  },
  "& .MuiSwitch-track": {
    backgroundColor: "#132729 !important",
    opacity: "1 !important",
  },
  "& .MuiSwitch-thumb": { backgroundColor: "#fff" },
};

const SideMenu: React.FC<DefaultAndAuthProps> = (props) => {
  const {
    admin,
    account,
    home,
    path,
    navigate,
    globalAppMode,
    setGlobalAppMode,
  } = props;

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const menuOpen = Boolean(anchorEl);

  const mode = admin?.is_super_admin
    ? globalAppMode
    : account?.type?.toUpperCase();

  let menuItems: ConfigMap = {};
  let access_level_text = "";

  if (admin) {
    if (admin.is_super_admin) {
      menuItems = { ...menuItems, ...SideMenuSuperAdmin };
      access_level_text = _("sidemenu", "access_level_super_admin");
    } else if (admin.is_account_admin) {
      menuItems = { ...menuItems, ...SideMenuAccountAdmin };
      access_level_text = _("sidemenu", "access_level_account_admin");
    } else if (admin.is_operations_manager) {
      menuItems = { ...menuItems, ...SideMenuOperationsManager };
      access_level_text = _("sidemenu", "access_level_operations_manager");
    } else if (admin.is_home_manager) {
      menuItems = { ...menuItems, ...SideMenuHomeManager };
      access_level_text = _("sidemenu", "access_level_home_manager");
    }
  }

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = () => {
    const currentLang = getCurrentLanguage();
    const newLang = currentLang === "en" ? "sv" : "en";
    setLanguage(newLang);
    handleMenuClose();
    window.location.reload();
  };

  const handleLogout = () => {
    handleMenuClose();
    signOut(props);
  };

  return (
    <>
      {isMobile && (
        <Box
          className={css.mobileTopBar}
          style={{
            backgroundColor: mode === "HEALTH" ? "#3E939F" : "#132729",
            color: "#fff",
          }}
        >
          <Typography variant="h6" className={css.mobileTopBarText}>
            AddYou
          </Typography>
          <IconButton
            sx={{ color: "#fff" }}
            aria-label="open drawer"
            edge="end"
            onClick={handleDrawerToggle}
            className={css.hamburgerButton}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      )}

      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          className={css.drawer}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
              backgroundColor: "white",
              border: "none",
              marginTop: "60px",
              height: "calc(100% - 60px)",
            },
          }}
        >
          <Box className={css.sideMenu}>
            {/* Logo Section */}
            <Box className={css.logoSection}>
              {mode === "HR" ? (
                <img
                  className={css.logoHr}
                  width={"70px"}
                  src={LogoHR}
                  alt="Add You"
                />
              ) : (
                <Box
                  className={css.logoWrapper}
                  onClick={() =>
                    window.open("https://www.addyou.se", "_blank", "noopener")
                  }
                >
                  <img className={css.logo} src={LogoHR} alt="Add You" />
                </Box>
              )}
            </Box>

            {admin?.is_super_admin ? (
              <Box
                className={css.companyBlock}
                style={{
                  backgroundColor:
                    globalAppMode === "HEALTH" ? "#3E939F" : "#132729",
                }}
              >
                <Typography className={css.companyLabel}>
                  {_("sidemenu", "company_label")}
                </Typography>

                <Box className={css.companyToggle}>
                  <Typography
                    className={css.companyToggleLabel}
                    style={{
                      fontWeight: globalAppMode === "HEALTH" ? 700 : 400,
                      fontSize: globalAppMode === "HEALTH" ? "15px" : "13px",
                    }}
                    aria-selected={globalAppMode === "HEALTH"}
                  >
                    {_("sidemenu", "company_health")}
                  </Typography>

                  <Switch
                    size="small"
                    checked={globalAppMode === "HR"}
                    onChange={(_, checked) => {
                      setGlobalAppMode(checked ? "HR" : "HEALTH");
                      navigate(Paths.ORGANISATION);
                    }}
                    sx={switchSx}
                  />

                  <Typography
                    className={css.companyToggleLabel}
                    style={{
                      fontWeight: globalAppMode === "HR" ? 700 : 400,
                      fontSize: globalAppMode === "HR" ? "15px" : "13px",
                    }}
                    aria-selected={globalAppMode === "HR"}
                  >
                    {_("sidemenu", "company_hr")}
                  </Typography>
                </Box>

                <Box className={css.companyInfo}>
                  <InfoOutlinedIcon sx={{ color: "#fff", fontSize: 16 }} />
                  <Typography className={css.companyInfoText}>
                    {_("sidemenu", "company_info")}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                className={css.companyBlock}
                style={{
                  backgroundColor: mode === "HEALTH" ? "#3E939F" : "#132729",
                  padding: "16px",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#fff",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  {admin?.is_operations_manager || admin?.is_home_manager
                    ? home?.name || account?.name || ""
                    : account?.name || ""}
                </Typography>
              </Box>
            )}

            <List className={css.menuList}>
              {Object.keys(menuItems)
                .filter((item) => {
                  if (item === "statistics") {
                    return true;
                  }
                  // Hide users menu items only in HR mode, using the same mode logic as everywhere else
                  if (item === "users" || item === "users2") {
                    const mode = admin?.is_super_admin
                      ? globalAppMode
                      : account?.type?.toUpperCase();
                    console.log("mode is", mode);
                    return mode !== "HR";
                  }
                  if (item === "awards") {
                    const mode = admin?.is_super_admin
                      ? globalAppMode
                      : account?.type?.toUpperCase();
                    return mode === "HR";
                  }
                  return true;
                })
                .map((item) => {
                  const mode = admin?.is_super_admin
                    ? globalAppMode
                    : account?.type?.toUpperCase();

                  let itemPath = menuItems[item];
                  if (item === "statistics") {
                    itemPath =
                      mode === "HR" ? Paths.HR_STATISTICS : Paths.STATISTICS;
                  }

                  let isSelected = path === itemPath;
                  // Mark 'organisation' selected only for organisation routes (prefix match)
                  if (
                    item === "organisation" &&
                    path.startsWith(Paths.ORGANISATION)
                  ) {
                    isSelected = true;
                  }
                  if (item === "employees" && path.includes("employees")) {
                    isSelected = true;
                  }
                  if (
                    item === "sub_organisations" &&
                    path.includes("sub-organisations")
                  ) {
                    isSelected = true;
                  }
                  // Determine label based on app mode or account type
                  let displayLabel = _("sidemenu", item);
                  if (
                    ["organisation", "sub_organisations", "employees"].includes(
                      item,
                    )
                  ) {
                    // Use consistent mode logic everywhere
                    if (mode === "HEALTH") {
                      displayLabel = _("sidemenu", "health_" + item);
                    } else if (mode === "HR") {
                      displayLabel = _("sidemenu", "hr_" + item);
                    }
                  }

                  let subtext = "";
                  if (item === "account") {
                    subtext = account?.name || "";
                  } else if (item !== "statistics") {
                    subtext = home?.name || "";
                  }

                  return (
                    <ListItem
                      key={item}
                      disablePadding
                      className={css.menuItem}
                    >
                      <ListItemButton
                        selected={isSelected}
                        onClick={() => {
                          // If an operations manager clicks "Sub organisations" (accommodations),
                          // open the Add Sub-organisation page directly instead of the list.
                          if (
                            item === "sub_organisations" &&
                            admin?.is_operations_manager
                          ) {
                            handleNavigate(Paths.ADD_SUB_ORGANISATIONS);
                          } else {
                            handleNavigate(itemPath);
                          }
                        }}
                        className={css.menuButton}
                        sx={{
                          "&.Mui-selected": {
                            backgroundColor: "#F7F7FB",
                            borderRight: "4px solid #3d939f",
                            paddingLeft: "12px",
                            "& .MuiListItemText-primary": {
                              fontWeight: 600,
                            },
                          },
                          "&.Mui-selected:hover": {
                            backgroundColor: "#F7F7FB",
                          },
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <ListItemText
                          primary={displayLabel}
                          secondary={subtext}
                          primaryTypographyProps={{
                            fontSize: "15px",
                            fontWeight: isSelected ? 600 : 400,
                            color: "#000",
                          }}
                          secondaryTypographyProps={{
                            fontSize: "12px",
                            color: "#666",
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}{" "}
            </List>

            <Box className={css.userSection}>
              <Box className={css.userInfo}>
                <Box>
                  <Typography variant="body1" className={css.userName}>
                    {admin?.given_name && admin?.family_name
                      ? `${admin.given_name} ${admin.family_name}`
                      : "User"}
                  </Typography>
                  <Typography variant="body2" className={css.userRole}>
                    {access_level_text}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={handleMenuOpen}>
                  <MoreVertIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          className={css.drawer}
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
              backgroundColor: "white",
              border: "none",
            },
          }}
        >
          <Box className={css.sideMenu}>
            <Box className={css.logoSection}>
              {mode === "HR" ? (
                <img
                  className={css.logoHr}
                  width={"80px"}
                  src={LogoHR}
                  alt="Add You"
                />
              ) : (
                <Box
                  className={css.logoWrapper}
                  onClick={() =>
                    window.open("https://www.addyou.se", "_blank", "noopener")
                  }
                >
                  <img className={css.logo} src={Logo} alt="Add You" />
                </Box>
              )}
            </Box>

            {/* Company selector (uses globalAppMode from App.tsx) */}
            {admin?.is_super_admin ? (
              <Box
                className={css.companyBlock}
                style={{
                  backgroundColor:
                    globalAppMode === "HEALTH" ? "#3E939F" : "#132729",
                }}
              >
                <Typography className={css.companyLabel}>
                  {_("sidemenu", "company_label")}
                </Typography>

                <Box className={css.companyToggle}>
                  <Typography
                    className={css.companyToggleLabel}
                    style={{
                      fontWeight: globalAppMode === "HEALTH" ? 700 : 400,
                      fontSize: globalAppMode === "HEALTH" ? "15px" : "13px",
                    }}
                    aria-selected={globalAppMode === "HEALTH"}
                  >
                    {_("sidemenu", "company_health")}
                  </Typography>

                  <Switch
                    size="small"
                    checked={globalAppMode === "HR"}
                    onChange={(_, checked) => {
                      setGlobalAppMode(checked ? "HR" : "HEALTH");
                      navigate(Paths.ORGANISATION);
                    }}
                    sx={switchSx}
                  />

                  <Typography
                    className={css.companyToggleLabel}
                    style={{
                      fontWeight: globalAppMode === "HR" ? 700 : 400,
                      fontSize: globalAppMode === "HR" ? "15px" : "13px",
                    }}
                    aria-selected={globalAppMode === "HR"}
                  >
                    {_("sidemenu", "company_hr")}
                  </Typography>
                </Box>

                <Box className={css.companyInfo}>
                  <InfoOutlinedIcon sx={{ color: "#fff", fontSize: 16 }} />
                  <Typography className={css.companyInfoText}>
                    {_("sidemenu", "company_info")}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                className={css.companyBlock}
                style={{
                  backgroundColor:
                    account?.type === "health" ? "#3E939F" : "#132729",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#fff",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  {admin?.is_operations_manager || admin?.is_home_manager
                    ? home?.name || account?.name || ""
                    : account?.name || ""}
                </Typography>
              </Box>
            )}

            {/* Menu Items */}
            <List className={css.menuList}>
              {Object.keys(menuItems)
                .filter((item) => {
                  if (item === "statistics") {
                    return true;
                  }
                  // Hide users menu items only in HR mode, using the same mode logic as everywhere else
                  if (item === "users" || item === "users2") {
                    const mode = admin?.is_super_admin
                      ? globalAppMode
                      : account?.type?.toUpperCase();
                    return mode !== "HR";
                  }
                  if (item === "awards") {
                    const mode = admin?.is_super_admin
                      ? globalAppMode
                      : account?.type?.toUpperCase();
                    return mode === "HR";
                  }
                  return true;
                })
                .map((item) => {
                  const mode = admin?.is_super_admin
                    ? globalAppMode
                    : account?.type?.toUpperCase();

                  let itemPath = menuItems[item];
                  if (item === "statistics") {
                    itemPath =
                      mode === "HR" ? Paths.HR_STATISTICS : Paths.STATISTICS;
                  }

                  let isSelected = path === itemPath;
                  // Mark 'organisation' selected only for organisation routes (prefix match)
                  if (
                    item === "organisation" &&
                    path.startsWith(Paths.ORGANISATION)
                  ) {
                    isSelected = true;
                  }
                  if (item === "employees" && path.includes("employees")) {
                    isSelected = true;
                  }
                  if (
                    item === "sub_organisations" &&
                    path.includes("sub-organisations")
                  ) {
                    isSelected = true;
                  }
                  // Determine label based on app mode or account type
                  let displayLabel = _("sidemenu", item);
                  if (
                    ["organisation", "sub_organisations", "employees"].includes(
                      item,
                    )
                  ) {
                    // For super admin, use globalAppMode; for others, use account type
                    if (mode === "HEALTH") {
                      displayLabel = _("sidemenu", "health_" + item);
                    } else if (mode === "HR") {
                      displayLabel = _("sidemenu", "hr_" + item);
                    }
                  }

                  return (
                    <ListItem
                      key={item}
                      disablePadding
                      className={css.menuItem}
                    >
                      <ListItemButton
                        selected={isSelected}
                        onClick={() => handleNavigate(itemPath)}
                        className={css.menuButton}
                        sx={{
                          "&.Mui-selected": {
                            backgroundColor: "#F7F7FB",
                            borderRight: "4px solid #3d939f",
                            paddingLeft: "12px",
                            "& .MuiListItemText-primary": {
                              fontWeight: 600,
                            },
                          },
                          "&.Mui-selected:hover": {
                            backgroundColor: "#F7F7FB",
                          },
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <ListItemText
                          primary={displayLabel}
                          primaryTypographyProps={{
                            fontSize: "15px",
                            fontWeight: isSelected ? 600 : 400,
                            color: "#000",
                          }}
                          secondaryTypographyProps={{
                            fontSize: "12px",
                            color: "#666",
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
            </List>

            <Box className={css.userSection}>
              <Box className={css.userInfo}>
                <Box>
                  <Typography variant="body1" className={css.userName}>
                    {admin?.given_name && admin?.family_name
                      ? `${admin.given_name} ${admin.family_name}`
                      : "User"}
                  </Typography>
                  <Typography variant="body2" className={css.userRole}>
                    {access_level_text}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={handleMenuOpen}>
                  <MoreVertIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Drawer>
      )}

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleLanguageChange}>
          {getCurrentLanguage() === "en" ? "🇸🇪 Svenska" : "🇬🇧 English"}
        </MenuItem>
        <MenuItem onClick={handleLogout}>{_("general", "btn_logout")}</MenuItem>
      </Menu>
    </>
  );
};

export default SideMenu;
